import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { bibleApi } from "./bible-api";
import { Occasion, OccasionVerse } from "./types";
import AddOccasionVerseModal from "./components/AddOccasionVerseModal";

export default function OccasionVerses() {
    const [occasions, setOccasions] = useState<Occasion[]>([]);
    const [occasionVerses, setOccasionVerses] = useState<OccasionVerse[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedOccasionFilter, setSelectedOccasionFilter] = useState<number | null>(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [occasionsData, versesData] = await Promise.all([
                bibleApi.getAllOccasions(),
                bibleApi.getAllOccasionVerses(),
            ]);

            setOccasions(occasionsData);
            setOccasionVerses(versesData);
        } catch (error: any) {
            console.log(error)
            toast.error("Failed to load occasions and verses");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Are you sure you want to delete this verse from the occasion?")) {
            return;
        }

        try {
            await bibleApi.deleteOccasionVerse(id);
            toast.success("Verse removed from occasion successfully");
            fetchData();
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Failed to delete verse");
        }
    };

    const handleMarkAsDefault = async (verse: OccasionVerse) => {
        try {
            await bibleApi.markVerseAsDefault(verse.id, verse.occasion_id);
            toast.success("Verse marked as default successfully");
            fetchData();
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Failed to mark verse as default");
        }
    };

    const filteredVerses = selectedOccasionFilter
        ? occasionVerses.filter((v) => v.occasion_id === selectedOccasionFilter)
        : occasionVerses;

    const getOccasionName = (occasionId: number) => {
        const occasion = occasions.find((o) => o.id === occasionId);
        return occasion?.name || `Occasion ${occasionId}`;
    };

    const getOccasionType = (occasionId: number) => {
        const occasion = occasions.find((o) => o.id === occasionId);
        return occasion?.type || "";
    };

    const groupedByOccasion = filteredVerses.reduce((acc, verse) => {
        if (!acc[verse.occasion_id]) {
            acc[verse.occasion_id] = [];
        }
        acc[verse.occasion_id].push(verse);
        return acc;
    }, {} as Record<number, OccasionVerse[]>);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
            <div className="max-w-[1600px] mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                                Verses Management (Occasion)
                            </h1>
                            <p className="text-gray-600 dark:text-gray-300">
                                Manage Bible verses assigned to occasions and anniversaries
                            </p>
                        </div>
                        <button
                            onClick={() => setModalOpen(true)}
                            className="btn-primary px-4 py-2 flex items-center space-x-2"
                        >
                            <i className="fa-solid fa-plus"></i>
                            <span>Add Verse to Occasion</span>
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <div className="flex items-center gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Filter by Occasion
                            </label>
                            <select
                                value={selectedOccasionFilter || ""}
                                onChange={(e) => setSelectedOccasionFilter(e.target.value ? Number(e.target.value) : null)}
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white"
                            >
                                <option value="">All Occasions</option>
                                {occasions.map((occasion) => (
                                    <option key={occasion.id} value={occasion.id}>
                                        {occasion.name} {occasion.type && `(${occasion.type})`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {selectedOccasionFilter && (
                            <button
                                onClick={() => setSelectedOccasionFilter(null)}
                                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 mt-6"
                            >
                                <i className="fa-solid fa-times mr-2"></i>
                                Clear Filter
                            </button>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                    ) : filteredVerses.length === 0 ? (
                        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                            {selectedOccasionFilter
                                ? "No verses found for the selected occasion"
                                : "No verses assigned to occasions yet. Click 'Add Verse to Occasion' to get started."}
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {Object.entries(groupedByOccasion).map(([occasionId, verses]) => (
                                <div
                                    key={occasionId}
                                    className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                                >
                                    {/* Occasion Header */}
                                    <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                    {getOccasionName(Number(occasionId))}
                                                </h3>
                                                {getOccasionType(Number(occasionId)) && (
                                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                        Type: {getOccasionType(Number(occasionId))}
                                                    </p>
                                                )}
                                            </div>
                                            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
                                                {verses.length} {verses.length === 1 ? "verse" : "verses"}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Verses List */}
                                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {verses.map((verse) => (
                                            <div
                                                key={verse.id}
                                                className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                            <span className="font-semibold text-blue-600 dark:text-blue-400">
                                                                {verse.book_name || `Book ${verse.book_index}`} {verse.chapter_index}:{verse.verse_index}
                                                            </span>
                                                            {verse.bible_version && (
                                                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                                                    ({verse.bible_name})
                                                                </span>
                                                            )}
                                                            {verse.is_default ? (
                                                                <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-medium flex items-center gap-1">
                                                                    <i className="fa-solid fa-star"></i>
                                                                    Default
                                                                </span>
                                                            ) : (
                                                                <button
                                                                    onClick={() => handleMarkAsDefault(verse)}
                                                                    className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 rounded-full text-xs font-medium flex items-center gap-1 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                                                                    title="Click to mark as default"
                                                                >
                                                                    <i className="fa-solid fa-star"></i>
                                                                    Default
                                                                </button>
                                                            )}

                                                            {verse.overrideLanguageCode && (
                                                                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs text-gray-600 dark:text-gray-400">
                                                                    {verse.overrideLanguageCode}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {verse.verse_text && (
                                                            <p className="text-gray-800 dark:text-gray-200 leading-relaxed mb-2">
                                                                {verse.verse_text}
                                                            </p>
                                                        )}
                                                        {verse.overrideText && (
                                                            <p className="text-gray-800 dark:text-gray-200 leading-relaxed mb-2">
                                                                {verse.overrideText}
                                                            </p>
                                                        )}
                                                        {verse.createdAt && (
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                                Added: {new Date(verse.createdAt).toLocaleDateString()}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="ml-4 flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleDelete(verse.id)}
                                                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                                                            title="Delete verse"
                                                        >
                                                            <i className="fa-solid fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <AddOccasionVerseModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSuccess={fetchData}
                occasions={occasions}
            />
        </div>
    );
}

